import { useState } from 'react'

import {
  Form,
  InputLabel,
  Space,
  SubText,
  TextButton,
  TextLabel,
} from '@procurify/ui'
import { theme } from '@procurify/ui-theme'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'

import { PriceFormField } from '@webapp/components/PriceField'
import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { Price } from '@webapp/core/components/Price'
import {
  type Currency,
  type CurrencyName,
} from '@webapp/core/entities/external/Currency'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFormatPrice, useProcurifyIntl } from '@webapp/hooks'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import {
  parsePercentageOrZero,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import {
  ExpenseMileageConfigurationTaxTypes,
  MileageRatePresetUnits,
  UnitsOfDistance,
  type ExpenseMileageConfiguration,
} from '@webapp/modules/expense/entities'
import { TaxFields } from './TaxFields'
import { MileageRatePresetSelect } from '../../MileageRatePresetSelect'
import { type ExpenseLineItemFormSchema } from '../ExpenseLineItemForm'
import messages from '../messages'

const MileageFieldsModes = {
  PRESET: 'preset',
  CUSTOM: 'custom',
} as const
type MileageFieldsMode = Enum<typeof MileageFieldsModes>

interface IMileageFieldsProps {
  currencyName: CurrencyName
  selectedCurrency: Currency | null
  onCurrencyChange: (currency: Currency | null) => void
  unitOptions: { label: string; value: string }[]
  computedSubtotal: number
  computedTotal: number
  isSubmitting: boolean
  canOverrideTaxAmount: boolean
  mileageConfiguration: Nullable<ExpenseMileageConfiguration>
}

export const MileageFields = ({
  currencyName,
  selectedCurrency,
  onCurrencyChange,
  unitOptions,
  computedSubtotal,
  computedTotal,
  isSubmitting,
  canOverrideTaxAmount,
  mileageConfiguration,
}: IMileageFieldsProps) => {
  const intl = useProcurifyIntl()
  const formatPrice = useFormatPrice()
  const formik = useFormikContext<ExpenseLineItemFormSchema>()

  const { reclaimable_expense_mileage_tax } = useSelector(
    makeSelectFeatureSwitches()
  )
  const isReclaimableMileageTaxEnabled = reclaimable_expense_mileage_tax
  const reclaimablePreset = formik.values.mileageRatePreset
  const reclaimableTax = reclaimablePreset?.tax
  const hasDistance = (formik.values.distance ?? 0) > 0

  const isCustomMileageSet =
    formik.values.mileageRate !== 0 && !formik.values.mileageRatePreset

  const [activeMode, setActiveMode] = useState<MileageFieldsMode>(
    !mileageConfiguration?.forcePresetRates && isCustomMileageSet
      ? MileageFieldsModes.CUSTOM
      : MileageFieldsModes.PRESET
  )
  const showTaxFields =
    !isReclaimableMileageTaxEnabled &&
    (!mileageConfiguration ||
      mileageConfiguration?.taxType ===
        ExpenseMileageConfigurationTaxTypes.USER_CHOSEN_TAX)
  const showUseCustomRateButton = !mileageConfiguration?.forcePresetRates

  return (
    <Space>
      {activeMode === MileageFieldsModes.PRESET && (
        <Space>
          <>
            <Space align='center' justify='end' direction='horizontal'>
              <InputLabel htmlFor='mileageRate' required>
                {intl.formatMessage(messages.mileageRateLabel)}
              </InputLabel>
              <Space.Item width={2 / 3}>
                <MileageRatePresetSelect
                  value={formik.values.mileageRatePreset}
                  onChange={(opt) => {
                    formik.setFieldValue('mileageRatePreset', opt)
                    formik.setFieldValue('mileageRate', opt?.rate)
                    formik.setFieldValue('unitOfDistanceStr', {
                      value:
                        opt?.unit === MileageRatePresetUnits.MILES
                          ? UnitsOfDistance.MILES
                          : UnitsOfDistance.KILOMETERS,
                      label:
                        opt?.unit === MileageRatePresetUnits.MILES
                          ? intl.formatMessage(messages.unitMi)
                          : intl.formatMessage(messages.unitKm),
                    })
                    formik.setFieldValue('currency', opt?.currency ?? null)
                  }}
                  isDisabled={isSubmitting}
                  menuPortalTarget={document.body}
                />
              </Space.Item>
            </Space>
            {showUseCustomRateButton && (
              <Space direction='horizontal' justify='end'>
                <TextButton
                  type='button'
                  onClick={() => {
                    formik.setFieldValue('mileageRatePreset', null)
                    setActiveMode(MileageFieldsModes.CUSTOM)
                  }}
                >
                  {intl.formatMessage(messages.useCustomMileageRateButtonLabel)}
                </TextButton>
              </Space>
            )}
          </>

          <Space align='center' justify='end' direction='horizontal'>
            <InputLabel htmlFor='distance' required>
              {intl.formatMessage(messages.distanceWithUnitLabel, {
                unit: formik.values.unitOfDistanceStr.value,
              })}
            </InputLabel>
            <Space.Item width={1 / 3}>
              <Form.NumberField
                id='distance'
                name='distance'
                disabled={isSubmitting}
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </Space.Item>
          </Space>
        </Space>
      )}

      {activeMode === MileageFieldsModes.CUSTOM && (
        <Space>
          <>
            <Space align='center' justify='end' direction='horizontal'>
              <InputLabel htmlFor='mileageRate' required>
                {intl.formatMessage(messages.mileageRateLabel)}
              </InputLabel>
              <Space.Item width={1 / 3}>
                <PriceFormField
                  id='mileageRate'
                  name='mileageRate'
                  disabled={isSubmitting}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </Space.Item>
              <Space.Item width={1 / 3}>
                <CurrencySelect
                  inputId='mileageFields-currency-select'
                  value={selectedCurrency}
                  onChange={onCurrencyChange}
                  isDisabled={isSubmitting}
                  menuPortalTarget={document.body}
                />
              </Space.Item>
            </Space>
            <Space direction='horizontal' justify='end'>
              <TextButton
                type='button'
                onClick={() => {
                  formik.setFieldValue('mileageRate', 0)
                  setActiveMode(MileageFieldsModes.PRESET)
                }}
              >
                {intl.formatMessage(messages.useMileageRatePresetButtonText)}
              </TextButton>
            </Space>
          </>

          <Space align='center' justify='end' direction='horizontal'>
            <InputLabel htmlFor='distance' required>
              {intl.formatMessage(messages.distanceLabel)}
            </InputLabel>
            <Space.Item width={1 / 3}>
              <Form.NumberField
                id='distance'
                name='distance'
                disabled={isSubmitting}
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </Space.Item>
            <Space.Item width={1 / 3}>
              <Form.Select
                id='unitOfDistanceStr'
                name='unitOfDistanceStr'
                options={unitOptions}
                disabled={isSubmitting}
              />
            </Space.Item>
          </Space>
        </Space>
      )}

      {isReclaimableMileageTaxEnabled ? (
        <Space direction='vertical' size='medium'>
          <Space align='center' justify='end' direction='horizontal'>
            <InputLabel>{intl.formatMessage(messages.totalLabel)}</InputLabel>
            <Price
              price={computedTotal}
              currency={currencyName}
              fontSize={theme.fontSizes.body2}
            />
          </Space>

          {canOverrideTaxAmount && reclaimableTax && (
            <Space direction='vertical' size='medium'>
              <Space align='center' justify='end' direction='horizontal'>
                <InputLabel>
                  {intl.formatMessage(messages.advisoryFuelRateLabel)}
                </InputLabel>
                <SubText bold noWrap inline autoMargin={false}>
                  {intl.formatMessage(messages.advisoryFuelRateValue, {
                    rate: formatPrice(reclaimablePreset?.advisoryFuelRate ?? 0),
                    currencyName,
                    unit: formik.values.unitOfDistanceStr.value,
                  })}
                </SubText>
              </Space>

              <Space align='center' justify='end' direction='horizontal'>
                <InputLabel>{intl.formatMessage(messages.taxLabel)}</InputLabel>
                <SubText bold noWrap inline autoMargin={false}>
                  {intl.formatMessage(messages.mileageTaxValue, {
                    taxName: reclaimableTax.name,
                    percentage: round2(
                      parsePercentageOrZero(reclaimableTax.percentage) * 100
                    ),
                  })}
                </SubText>
              </Space>

              <Space align='center' justify='end' direction='horizontal'>
                <InputLabel htmlFor='taxAmount'>
                  {intl.formatMessage(messages.reclaimableTaxAmountLabel, {
                    currencyName,
                  })}
                </InputLabel>
                <Space.Item width={1 / 3}>
                  {hasDistance ? (
                    <PriceFormField
                      id='taxAmount'
                      name='taxAmount'
                      disabled={isSubmitting}
                      decimalPlaces={2}
                      inputProps={{
                        allowNegative: false,
                        style: { textAlign: 'right' },
                      }}
                    />
                  ) : (
                    <Price
                      textAlign='right'
                      price={0}
                      currency={currencyName}
                      fontSize={theme.fontSizes.body2}
                      fontWeight='fontWeightBold'
                    />
                  )}
                </Space.Item>
              </Space>
            </Space>
          )}
        </Space>
      ) : (
        <Space direction='vertical' size='medium'>
          {showTaxFields && (
            <Space direction='vertical' size='medium'>
              <Space align='center' justify='end' direction='horizontal'>
                <TextLabel bold>
                  {intl.formatMessage(messages.subtotalLabel)}
                </TextLabel>
                <Price
                  price={computedSubtotal}
                  currency={currencyName}
                  fontSize={theme.fontSizes.body2}
                />
              </Space>

              <TaxFields
                isSubmitting={isSubmitting}
                canOverrideTaxAmount={canOverrideTaxAmount}
              />
            </Space>
          )}

          <Space align='center' justify='end' direction='horizontal'>
            <TextLabel bold>
              {intl.formatMessage(messages.totalLabel)}
            </TextLabel>
            <Price
              price={computedTotal}
              currency={currencyName}
              fontSize={theme.fontSizes.body2}
            />
          </Space>
        </Space>
      )}
    </Space>
  )
}
